/**
 * The status-code composition of ONE window, as a pie.
 *
 * `StatusTimeSeries` answers "how did the mix change across windows"; this
 * answers "what is this window actually made of". They are different questions,
 * which is why both exist: the stacked bars trade per-code precision for
 * comparison across six windows, and at 0.5% a slice of a bar is a hairline you
 * cannot hover accurately.
 *
 * Every code gets its own slice rather than being folded into the five buckets.
 * A 429 and a 402 are both "throttled" for health purposes but they mean very
 * different things operationally, and the bucket colours are reused across the
 * codes inside a bucket so the pie still reads as the same colour language as
 * the table.
 *
 * Colours are resolved to literal values, like the other chart: canvas and SVG
 * renderers cannot read CSS variables.
 */
import { Fragment, Suspense, lazy, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { WINDOW_LABEL_KEYS } from '../api/types'
import type { EndpointSummary, WindowId } from '../api/types'
import { useI18n } from '../i18n'
import { bucketFor, numberFormat, percent, readBucketPalette, readThemeColor } from './statusColors'

/**
 * Fill the `{{placeholders}}` left in an already-translated sentence with React
 * nodes.
 *
 * The summary line under the chart puts two numbers inside one sentence and
 * styles them. Building that by concatenating "N" + " requests across " + "M" +
 * " status codes" would hard-code English word order into a Chinese string, so
 * the sentence is one key and this puts the styled numbers back into the holes
 * the runtime deliberately leaves unfilled for params it was not given.
 */
function fill(template: string, nodes: Record<string, ReactNode>): ReactNode[] {
  return template.split(/(\{\{\w+\}\})/g).map((part, i) => {
    const name = /^\{\{(\w+)\}\}$/.exec(part)?.[1]
    return <Fragment key={i}>{name === undefined ? part : nodes[name]}</Fragment>
  })
}

const ReactECharts = lazy(() => import('echarts-for-react'))

export interface StatusSlice {
  name: string
  value: number
  itemStyle: { color: string }
  /**
   * The slice's own colour, repeated for the hover state.
   *
   * This is not redundant. ECharts resolves the emphasis state against the
   * SERIES `itemStyle`, not the data item's, and the series has no colour —
   * the colours live per slice. A series-level `emphasis.itemStyle` without a
   * colour therefore resolved to no fill, and the hovered sector rendered
   * white until the pointer left. Pinning the colour on the item's own
   * emphasis is what actually holds it.
   */
  emphasis: { itemStyle: { color: string; shadowBlur: number; shadowColor: string } }
}

/**
 * One slice per status code, largest first.
 *
 * Codes with a zero count are dropped: a 0% slice is invisible but still takes
 * a legend entry and a tooltip row, which is noise. A window we never measured
 * (`total === null`) yields nothing at all — distinct from a measured window
 * that happened to see no traffic.
 */
export function buildSlices(
  summary: EndpointSummary | undefined,
  palette: Record<string, string>,
): StatusSlice[] {
  if (!summary || summary.total === null) return []
  return Object.entries(summary.by_status)
    .map(([code, count]) => ({ code: Number(code), count }))
    .filter((s) => Number.isFinite(s.code) && s.count > 0)
    .sort((a, b) => b.count - a.count)
    .map((s) => {
      const color = palette[bucketFor(s.code)]
      return {
        name: String(s.code),
        value: s.count,
        itemStyle: { color },
        emphasis: {
          itemStyle: { color, shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.22)' },
        },
      }
    })
}

export interface StatusPieProps {
  /** The window being described. Drives the heading and the empty-state copy. */
  window: WindowId
  /** That window's summary, or undefined when the backend has not cached it. */
  summary: EndpointSummary | undefined
  height?: number
}

export function StatusPie({ window, summary, height = 300 }: StatusPieProps) {
  const { t } = useI18n()
  const [normalize, setNormalize] = useState(true)
  const palette = useMemo(() => readBucketPalette(), [])
  // Not a hardcoded '#fff': that was white in dark mode too, and a white ring
  // widening over a thin slice is what made hovering look like a flash.
  const separator = useMemo(() => readThemeColor('--surface', '#ffffff'), [])
  const slices = useMemo(() => buildSlices(summary, palette), [summary, palette])
  const total = slices.reduce((sum, s) => sum + s.value, 0)

  const option = useMemo(
    () => ({
      animation: false,
      tooltip: {
        trigger: 'item',
        // Same reason as the bar chart: inside a scrollable panel an unconfined
        // tooltip is clipped by the overflow.
        confine: true,
        valueFormatter: (value: unknown) =>
          typeof value !== 'number'
            ? '—'
            : normalize
              ? percent(total === 0 ? 0 : value / total)
              : numberFormat.format(value),
      },
      legend: { type: 'scroll', bottom: 0, icon: 'roundRect' },
      // Hover must not animate. `animation: false` above only covers the
      // initial draw; the enter/leave transition between normal and emphasis
      // states has its own timing, and that transition is what was read as the
      // slice blinking white and coming back.
      stateAnimation: { duration: 0 },
      series: [
        {
          type: 'pie',
          // A donut, not a full pie: the hole carries the total, which is the
          // number you would otherwise have to add up from the slices.
          radius: ['46%', '72%'],
          center: ['50%', '46%'],
          avoidLabelOverlap: true,
          itemStyle: { borderColor: separator, borderWidth: 2 },
          // Geometry only. The hover COLOUR is set per slice in `buildSlices`,
          // because emphasis resolves against this series-level style and the
          // series has no colour to give it — see the note on StatusSlice.
          // `scale: false` stops the sector jumping outward, which on a
          // 2px-separated donut reads as a flicker of its own.
          emphasis: { scale: false },
          label: {
            formatter: normalize
              ? (p: { name: string; percent?: number }) =>
                  `${p.name}  ${(p.percent ?? 0).toFixed(1)}%`
              : (p: { name: string; value?: number }) =>
                  `${p.name}  ${numberFormat.format(p.value ?? 0)}`,
          },
          data: slices,
        },
      ],
    }),
    [slices, normalize, total, separator],
  )

  return (
    <section aria-label={t('dashboard.pie.regionLabel')} className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h3 className="text-sm font-semibold text-fg">
          {t('dashboard.pie.heading')}{' '}
          {/* The window name comes from `WINDOW_LABEL_KEYS`, the api area's
              table of KEYS: the heading is always in the reader's language,
              and the compiler checks the key. */}
          <span className="font-normal text-muted">
            {t('dashboard.pie.headingWindow', {
              window: t(WINDOW_LABEL_KEYS[window]),
            })}
          </span>
        </h3>
        <div className="flex gap-1" role="group" aria-label={t('dashboard.pie.scaleGroup')}>
          <PieScaleButton active={normalize} onClick={() => setNormalize(true)}>
            {t('dashboard.scale.shares')}
          </PieScaleButton>
          <PieScaleButton active={!normalize} onClick={() => setNormalize(false)}>
            {t('dashboard.scale.counts')}
          </PieScaleButton>
        </div>
      </div>

      {slices.length === 0 ? (
        <p
          role="status"
          className="rounded-md border border-border bg-hatch px-3 py-6 text-center text-sm text-muted"
        >
          {summary === undefined
            ? t('dashboard.pie.emptyNotCached')
            : t('dashboard.pie.emptyNoRequests')}
        </p>
      ) : (
        <>
          <div style={{ height }} className="rounded-md border border-border p-1">
            <Suspense fallback={<div className="h-full w-full animate-pulse rounded bg-border" />}>
              <ReactECharts
                option={option}
                notMerge
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            </Suspense>
          </div>
          <p className="text-xs text-muted">
            {fill(t('dashboard.pie.footer', { count: slices.length }), {
              total: <span className="num text-fg">{numberFormat.format(total)}</span>,
              codes: <span className="num text-fg">{slices.length}</span>,
            })}
          </p>
        </>
      )}
    </section>
  )
}

function PieScaleButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded border px-1.5 py-0.5 text-xs ${
        active ? 'border-accent text-fg' : 'border-border text-muted hover:text-fg'
      }`}
    >
      {children}
    </button>
  )
}
